import React, { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { PDFViewer } from "@react-pdf/renderer";
import { ClimbPlanPDF } from "@/components/pdf/ClimbPlanPDF";
import type { ClimbPlanPDFData } from "@/pages/planner/climb-plan/review/pdf/climbPlanPDFData";
/**
 * Stand-ins for the two images a real plan carries.
 *
 * In the app the Hat card is a remote WEBP re-encoded by the pre-fetch and the
 * key's icon is a CMS upload; neither is reachable from a hand-written
 * fixture. Without them page one's hero draws a hole where its whole point is.
 */
import previewHatImage from "@/assets/images/planner/climb-plan/pdf/top-hat.png";
import previewKeyIcon from "@/assets/images/planner/climb-plan/pdf/keys-to-success.png";

/**
 * Two fixtures, because the two that matter are the extremes.
 *
 * MAXIMAL exercises every section at once with the longest content each can
 * hold — a 600-character reflection, six schools, five credentials, ten
 * courses, every perk and quirk — which is the only way to see where the page
 * actually breaks. MINIMAL is the climber whose district publishes no
 * programs: three sections disappear and the rest must close up behind them
 * rather than leaving a hole.
 *
 * Hand-written rather than captured from the app, so the file is readable and
 * does not go stale with a fixture district.
 */
const LOREM =
  "I want this career because I like building things and figuring out why they break. ".repeat(
    7,
  );

const MAXIMAL: ClimbPlanPDFData = {
  lastUpdated: "Oct 2, 2026",
  fileName: "climber_climb-plan_2026-10-02.pdf",
  climber: {
    name: "Ashlee Hauck",
    gradeLabel: "Grade 7",
    studentId: "123456789",
    schoolName: "Maple Grove Middle School",
    districtName: "Maple Grove Unified School District",
  },
  chosen: {
    program: "Robotics and Automation Technology",
    school: "Cedar Hollow High School",
  },
  reflection: {
    prompt:
      "How does your plan for this career fit your interests, your values, and the skills you want to build?",
    text: LOREM.slice(0, 600),
  },
  signature: { name: "Ashlee Hauck", signedOn: "Oct 2, 2026" },
  myCore: [
    {
      name: "Analyzer",
      description: "You like working out why things happen.",
      tier: "high",
      tierLabel: "High Interest",
      percentage: 88,
      color: "#078dee",
    },
    {
      name: "Creator",
      description: "You like making things that did not exist before.",
      tier: "some",
      tierLabel: "Some Interest",
      percentage: 54,
      color: "#8e33ff",
    },
  ],
  myWorkValues: [
    {
      name: "Being Independent",
      description: "You want room to decide how the work gets done.",
    },
    {
      name: "Building Relationships",
      description: "You want to work alongside people you trust.",
    },
  ],
  topHat: {
    displayName: "3D Printing Technician",
    shortDescription:
      "3D Printing Technicians turn computer files into physical objects - building medical implants and aerospace parts, one layer at a time.",
    imageSrc: previewHatImage,
    fitMessage:
      "You have 4 fits for 3D Printing Technician. Strong signals point to a real fit here - explore the details of this Hat to confirm what you're seeing.",
    core: [
      {
        name: "Doer",
        description: "Hands-on work with tools and machines.",
        tierLabel: "High Interest",
        isMatch: true,
      },
      { name: "Analyzer", tierLabel: "High Interest", isMatch: true },
      { name: "Creator", tierLabel: "Some Interest", isMatch: false },
      { name: "Helper", tierLabel: "Low Interest", isMatch: false },
      { name: "Persuader", tierLabel: "Low Interest", isMatch: false },
      { name: "Organizer", tierLabel: "Some Interest", isMatch: true },
    ],
    workValues: [
      {
        name: "Trust & Support",
        description: "A workplace that backs you up.",
        isMatch: true,
      },
      { name: "Accomplishing Goals", isMatch: false },
    ],
    perksAndQuirks: [
      {
        name: "You get to make real things",
        kind: "Perk",
        response: "That's for me",
        sentiment: "LIKE",
      },
      {
        name: "Prints fail, often",
        kind: "Quirk",
        response: "Yikes",
        sentiment: "DISLIKE",
      },
      {
        name: "Every job is a puzzle",
        kind: "Perk",
        response: "Not Important",
        sentiment: "DISLIKE",
      },
      // Unanswered, so the preview shows that state too.
      {
        name: "The room is loud",
        kind: "Quirk",
        response: null,
        sentiment: null,
      },
    ],
    badges: [
      {
        name: "Problem Solver",
        tooltip: "You like working out why things go wrong.",
      },
      { name: "Maker", tooltip: null },
      { name: "Detail Watcher", tooltip: "You notice what others walk past." },
    ],
  },
  connectProfile: {
    blocks: [
      {
        activityName: "Spirit/Pep/Cheerleading",
        categoryName: "Extracurriculars and Hobbies",
        duration: "2 - 3 years",
        description: "I have done this since fifth grade and I plan carefully.",
        likes: ["Leadership", "Musicality and Rhythm"],
        dislikes: ["Paperwork"],
      },
      {
        activityName: "Family, Pet, Child, or Home Care",
        categoryName: "Responsibilities",
        duration: "> 5 years",
        description: "I look after my younger brother after school every day.",
        likes: ["Problem Solving", "Curiosity"],
        dislikes: [],
      },
    ],
    reflectionPrompt:
      "How do the Building Blocks you chose connect to your career goals?",
    reflection: LOREM.slice(0, 400),
  },
  highSchoolPriorities: {
    programName: "Robotics and Automation Technology",
    programIntro:
      "This program prepares students to build, program and maintain automated systems used across manufacturing and healthcare.",
    schools: [
      "Ash Grove High School",
      "Brookline High School",
      "Cedar Hollow High School",
      "Dunmore High School",
      "Eastbrook High School",
      "Westvale High School",
    ],
    certifications: [
      {
        // The longest real credential name in the dev district: the case that
        // showed @react-pdf breaking a word across lines.
        name: "Certified SOLIDWORKS Associate - Additive Manufacturing",
        description: "Entry-level CAD certification.",
      },
      {
        name: "OSHA 10-Hour General Industry",
        description: "Workplace safety fundamentals.",
      },
      {
        name: "FANUC Certified Robot Operator",
        description: "Industrial robot operation.",
      },
      {
        name: "Certified Production Technician",
        description: "Manufacturing process basics.",
      },
      {
        name: "Autodesk Fusion 360 Certified User",
        description: "Parametric modelling.",
      },
    ],
    sampleCourses: Array.from({ length: 10 }, (_, i) => `Course ${i + 1}`),
    otherHats: [
      { name: "Industrial Machinery Mechanic", sentimentLabel: "Loved" },
      { name: "Robotics Engineer", sentimentLabel: null },
    ],
  },
  programSpotlight: [
    "Students in this program compete nationally every spring.",
    "Last year's cohort placed third at the state SkillsUSA championship.",
  ],
  keysToSuccess: {
    iconSrc: previewKeyIcon,
    name: "Challenge Yourself",
    description: "Take the harder option when you have the choice.",
    goalLabel: "Goal",
    reflectionPrompt:
      "Who or what can support you in achieving this goal in high school? (optional)",
    reflection:
      "My older sister took this program and can help me with the maths.",
  },
  ctsos: [
    {
      name: "SkillsUSA",
      paragraphs: [
        "SkillsUSA is a partnership of students, teachers and industry.",
        "Members compete in over one hundred trade and technical events.",
      ],
    },
  ],
  afterHighSchool: [
    { name: "Bachelor's Degree", definition: "A four-year university degree." },
    {
      name: "Apprenticeship",
      definition: "Paid work alongside classroom study.",
    },
    {
      name: "Career / Technical School",
      definition: "Hands-on training for a specific trade.",
    },
    { name: "Military Training", definition: "Service with job training." },
  ],
  afterHighSchoolReflection: {
    prompt: "Why you are considering these paths? (optional)",
    text: "I like building things, and an apprenticeship would pay me while I learn.",
  },
  skillsToBuild: {
    strength: {
      name: "Planning",
      typeLabel: "PowerSkill",
      description: "Working out the order things need to happen in.",
    },
    shoutOutLabel: "You have 2 Shout Outs",
    shoutOuts: [
      {
        message: "Ashlee organised the whole showcase single-handedly.",
        author: "U. Teacher",
      },
      {
        message: "Always three steps ahead of the rest of the group.",
        author: null,
      },
    ],
    goals: [
      {
        name: "Listening",
        typeLabel: "PowerSkill",
        description: "Taking in what others say.",
      },
      {
        name: "Data Collection & Recording",
        typeLabel: "ProSkill",
        description: "Keeping accurate records.",
      },
    ],
    reflectionPrompt: "How will you work on building these skills? (optional)",
    reflection: "I will ask for feedback after every group project this year.",
  },
  otherShoutOuts: [
    {
      heading: "Listening (1)",
      shoutOuts: [
        {
          message: "Stayed late to help the younger team debug their robot.",
          author: "R. Alvarez",
        },
      ],
    },
    {
      heading: "Data Collection & Recording (1)",
      shoutOuts: [
        {
          message: "Took careful notes for the whole group all semester.",
          author: null,
        },
      ],
    },
  ],
};

/** A climber whose district publishes no programs, and who signed nothing. */
const MINIMAL: ClimbPlanPDFData = {
  lastUpdated: null,
  fileName: "climber_climb-plan_2026-10-02.pdf",
  climber: {
    name: "Sam Reyes",
    gradeLabel: "Grade 6",
    studentId: null,
    schoolName: null,
    districtName: null,
  },
  chosen: { program: null, school: null },
  reflection: null,
  signature: null,
  myCore: [],
  myWorkValues: [],
  topHat: {
    displayName: "Conservation Scientist",
    shortDescription: "Conservation Scientists look after forests and parks.",
    // A coach's copy of the same plan: the fit is scored against whoever is
    // signed in, so it is not claimed at all.
    fitMessage: null,
    core: [{ name: "Doer", tierLabel: "Some Interest", isMatch: false }],
    workValues: [],
    perksAndQuirks: [],
    badges: [],
  },
  connectProfile: {
    blocks: [],
    reflectionPrompt:
      "How does your Core Personality connect to this career’s mix of types?",
    reflection: "My Core says I like being outdoors and that fits.",
  },
  highSchoolPriorities: null,
  programSpotlight: null,
  keysToSuccess: null,
  ctsos: [],
  afterHighSchool: [],
  afterHighSchoolReflection: {
    prompt: "Why you are considering these paths? (optional)",
    text: "",
  },
  skillsToBuild: null,
  otherShoutOuts: [],
};

const FIXTURES = { Maximal: MAXIMAL, Minimal: MINIMAL } as const;

export function ClimbPlanPDFPreviewPage() {
  const [which, setWhich] = useState<keyof typeof FIXTURES>("Maximal");

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
        <Typography variant="h6">Climb Plan PDF</Typography>
        {(Object.keys(FIXTURES) as Array<keyof typeof FIXTURES>).map((key) => (
          <Button
            key={key}
            variant={which === key ? "contained" : "outlined"}
            onClick={() => setWhich(key)}
          >
            {key}
          </Button>
        ))}
      </Stack>
      <PDFViewer style={{ flexGrow: 1, border: "none" }}>
        <ClimbPlanPDF data={FIXTURES[which]} />
      </PDFViewer>
    </Box>
  );
}

export default ClimbPlanPDFPreviewPage;
